/**
 * Compact "Skipping: X, Y" summary — F02 (SoporteFixes4 W5.3).
 *
 * Single rendering + formatting source shared by the Setup dialog's options
 * row and the results toolbar, so both surfaces always show the identical
 * string derived from the identical `ignoredProperties` array — never a
 * second hand-written derivation that can drift from
 * `getStandardIgnoredProperties()` / the user's own unticks.
 *
 * Renders nothing when nothing is ignored: an empty list truthfully means no
 * property class is being hidden, so there's nothing to disclose.
 */

type Props = {
  ignoredProperties: string[];
  onClick: () => void;
  testId: string;
  className?: string;
};

export function IgnoredPropertiesSummaryButton({
  ignoredProperties,
  onClick,
  testId,
  className = '',
}: Props) {
  if (ignoredProperties.length === 0) return null;

  const text = `Skipping: ${ignoredProperties.join(', ')}`;

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      // Fix round 1 (Minor): was a static generic string — the visible label
      // is `truncate`d, so a long list (Oracle Standard plus a few unticks)
      // needs the tooltip to carry the FULL text, not repeat a caption the
      // truncated label already gestures at.
      title={`${text} — click to change`}
      // R3-5: `max-w-[320px]` is gone. It never did the job it looked like it
      // did — `truncate` sets `overflow: hidden`, which gives a flex item an
      // automatic minimum size of 0, so a max-width cannot stop this shrinking
      // and this was the ONLY shrinkable child on both of its host rows. It
      // absorbed every width deficit and crushed to `Skippin…` while the fixed
      // buttons beside it overflowed their container anyway. Both hosts now
      // render it on its own row, so `max-w-full` + `min-w-0` is the right
      // cap: it takes the row it is given and truncates only when the list
      // genuinely exceeds it.
      className={`text-xs text-left text-muted-foreground hover:text-foreground hover:underline underline-offset-2 truncate min-w-0 max-w-full ${className}`}
    >
      {text}
    </button>
  );
}
